/**
 * IndexedDB Utility - 오프라인 데이터 저장소
 *
 * 운동 기록을 로컬에 저장하여 오프라인에서도 기록 가능
 * 온라인 복귀 시 자동 동기화
 */

const DB_NAME = 'hellgater-offline';
const DB_VERSION = 1;

// Store names
export const STORES = {
  PENDING_WORKOUTS: 'pending_workouts',
  CACHED_EXERCISES: 'cached_exercises',
  CACHED_ROUTINES: 'cached_routines',
  USER_PREFERENCES: 'user_preferences',
} as const;

type StoreName = typeof STORES[keyof typeof STORES];

// Pending workout interface
export interface PendingWorkout {
  id: string;
  exerciseId: number;
  sets: number;
  reps: number;
  weight: number;
  workoutDate: string;
  notes?: string;
  createdAt: string;
  syncStatus: 'pending' | 'syncing' | 'failed';
  retryCount: number;
  lastError?: string;
}

// Cached exercise interface
export interface CachedExercise {
  id: number;
  nameKo: string;
  nameEn: string;
  bodyPartId: number;
  difficulty?: string;
  cachedAt: string;
}

// Cached routine interface
export interface CachedRoutine {
  id: string;
  name: string;
  description?: string;
  exercises: Array<{
    exerciseId: number;
    exerciseName: string;
    defaultSets: number;
    defaultReps: number;
    defaultWeight: number | null;
  }>;
  cachedAt: string;
}

/**
 * IndexedDB 연결
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('IndexedDB open error:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Pending workouts store
      if (!db.objectStoreNames.contains(STORES.PENDING_WORKOUTS)) {
        const workoutStore = db.createObjectStore(STORES.PENDING_WORKOUTS, {
          keyPath: 'id',
        });
        workoutStore.createIndex('syncStatus', 'syncStatus', { unique: false });
        workoutStore.createIndex('workoutDate', 'workoutDate', { unique: false });
      }

      // Cached exercises store
      if (!db.objectStoreNames.contains(STORES.CACHED_EXERCISES)) {
        const exerciseStore = db.createObjectStore(STORES.CACHED_EXERCISES, {
          keyPath: 'id',
        });
        exerciseStore.createIndex('bodyPartId', 'bodyPartId', { unique: false });
      }

      // Cached routines store
      if (!db.objectStoreNames.contains(STORES.CACHED_ROUTINES)) {
        db.createObjectStore(STORES.CACHED_ROUTINES, { keyPath: 'id' });
      }

      // User preferences store
      if (!db.objectStoreNames.contains(STORES.USER_PREFERENCES)) {
        db.createObjectStore(STORES.USER_PREFERENCES, { keyPath: 'key' });
      }
    };
  });
}

/**
 * Generic CRUD operations
 */
async function getAll<T>(storeName: StoreName): Promise<T[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    transaction.oncomplete = () => db.close();
  });
}

async function get<T>(storeName: StoreName, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    transaction.oncomplete = () => db.close();
  });
}

async function put<T>(storeName: StoreName, data: T): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(data);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    transaction.oncomplete = () => db.close();
  });
}

async function remove(storeName: StoreName, key: IDBValidKey): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    transaction.oncomplete = () => db.close();
  });
}

async function clear(storeName: StoreName): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    transaction.oncomplete = () => db.close();
  });
}

async function getByIndex<T>(
  storeName: StoreName,
  indexName: string,
  value: IDBValidKey
): Promise<T[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(value);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    transaction.oncomplete = () => db.close();
  });
}

// =====================================
// Pending Workouts Operations
// =====================================

/**
 * 대기 중인 운동 기록 저장 (오프라인용)
 */
export async function savePendingWorkout(workout: Omit<PendingWorkout, 'id' | 'createdAt' | 'syncStatus' | 'retryCount'>): Promise<string> {
  const id = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const pendingWorkout: PendingWorkout = {
    ...workout,
    id,
    createdAt: new Date().toISOString(),
    syncStatus: 'pending',
    retryCount: 0,
  };

  await put(STORES.PENDING_WORKOUTS, pendingWorkout);
  return id;
}

/**
 * 대기 중인 운동 기록 조회
 */
export async function getPendingWorkouts(): Promise<PendingWorkout[]> {
  return getAll<PendingWorkout>(STORES.PENDING_WORKOUTS);
}

/**
 * 동기화 대기 중인 운동 기록만 조회
 */
export async function getWorkoutsToSync(): Promise<PendingWorkout[]> {
  return getByIndex<PendingWorkout>(STORES.PENDING_WORKOUTS, 'syncStatus', 'pending');
}

/**
 * 운동 기록 동기화 상태 업데이트
 */
export async function updateWorkoutSyncStatus(
  id: string,
  status: PendingWorkout['syncStatus'],
  error?: string
): Promise<void> {
  const workout = await get<PendingWorkout>(STORES.PENDING_WORKOUTS, id);
  if (workout) {
    workout.syncStatus = status;
    workout.retryCount = status === 'failed' ? workout.retryCount + 1 : workout.retryCount;
    workout.lastError = error;
    await put(STORES.PENDING_WORKOUTS, workout);
  }
}

/**
 * 동기화된 운동 기록 삭제
 */
export async function removePendingWorkout(id: string): Promise<void> {
  await remove(STORES.PENDING_WORKOUTS, id);
}

/**
 * 모든 대기 중인 운동 기록 삭제
 */
export async function clearPendingWorkouts(): Promise<void> {
  await clear(STORES.PENDING_WORKOUTS);
}

// =====================================
// Cached Exercises Operations
// =====================================

/**
 * 운동 목록 캐시
 */
export async function cacheExercises(exercises: CachedExercise[]): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.CACHED_EXERCISES, 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_EXERCISES);

    exercises.forEach((exercise) => {
      store.put({ ...exercise, cachedAt: new Date().toISOString() });
    });

    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => {
      db.close();
      resolve();
    };
  });
}

/**
 * 캐시된 운동 목록 조회
 */
export async function getCachedExercises(): Promise<CachedExercise[]> {
  return getAll<CachedExercise>(STORES.CACHED_EXERCISES);
}

/**
 * 부위별 캐시된 운동 조회
 */
export async function getCachedExercisesByBodyPart(bodyPartId: number): Promise<CachedExercise[]> {
  return getByIndex<CachedExercise>(STORES.CACHED_EXERCISES, 'bodyPartId', bodyPartId);
}

// =====================================
// Cached Routines Operations
// =====================================

/**
 * 루틴 캐시
 */
export async function cacheRoutine(routine: Omit<CachedRoutine, 'cachedAt'>): Promise<void> {
  await put(STORES.CACHED_ROUTINES, { ...routine, cachedAt: new Date().toISOString() });
}

/**
 * 캐시된 루틴 목록 조회
 */
export async function getCachedRoutines(): Promise<CachedRoutine[]> {
  return getAll<CachedRoutine>(STORES.CACHED_ROUTINES);
}

/**
 * 캐시된 루틴 조회
 */
export async function getCachedRoutine(id: string): Promise<CachedRoutine | undefined> {
  return get<CachedRoutine>(STORES.CACHED_ROUTINES, id);
}

// =====================================
// User Preferences Operations
// =====================================

interface UserPreference {
  key: string;
  value: any;
}

/**
 * 사용자 설정 저장
 */
export async function setPreference(key: string, value: any): Promise<void> {
  await put(STORES.USER_PREFERENCES, { key, value });
}

/**
 * 사용자 설정 조회
 */
export async function getPreference<T>(key: string): Promise<T | undefined> {
  const pref = await get<UserPreference>(STORES.USER_PREFERENCES, key);
  return pref?.value;
}

// =====================================
// Utility Functions
// =====================================

/**
 * IndexedDB 지원 여부 확인
 */
export function isIndexedDBSupported(): boolean {
  return 'indexedDB' in window;
}

/**
 * 모든 오프라인 데이터 삭제
 */
export async function clearAllOfflineData(): Promise<void> {
  const db = await openDB();
  const storeNames = Array.from(db.objectStoreNames);
  db.close();

  for (const storeName of storeNames) {
    await clear(storeName as StoreName);
  }
}

/**
 * 오프라인 데이터 크기 확인 (대략적)
 */
export async function getOfflineDataSize(): Promise<{
  pendingWorkouts: number;
  cachedExercises: number;
  cachedRoutines: number;
}> {
  const [pendingWorkouts, cachedExercises, cachedRoutines] = await Promise.all([
    getPendingWorkouts(),
    getCachedExercises(),
    getCachedRoutines(),
  ]);

  return {
    pendingWorkouts: pendingWorkouts.length,
    cachedExercises: cachedExercises.length,
    cachedRoutines: cachedRoutines.length,
  };
}

export default {
  savePendingWorkout,
  getPendingWorkouts,
  getWorkoutsToSync,
  updateWorkoutSyncStatus,
  removePendingWorkout,
  clearPendingWorkouts,
  cacheExercises,
  getCachedExercises,
  getCachedExercisesByBodyPart,
  cacheRoutine,
  getCachedRoutines,
  getCachedRoutine,
  setPreference,
  getPreference,
  isIndexedDBSupported,
  clearAllOfflineData,
  getOfflineDataSize,
};
